/**
 * Loads the fleet once for the whole shell and hands the same two lists to
 * every screen, so a vehicle edited on one page is already correct on the
 * other. Reads go through `src/db/fleet-store.ts` — nothing here talks to
 * Dexie directly.
 *
 * The lists also re-read themselves: they subscribe to the global reactive
 * store, so a vehicle whose status was tapped on the roster — or one that a
 * convoy just took out — is correct on every open screen at once.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  listSoldiers,
  listVehicles,
  reconcileSoldierRoles,
  reconcileSoldierRoster,
  reconcileVehicleRoster,
  seedSoldierRoster,
  seedVehicleRoster,
} from '../../db/fleet-store';
import { useStoreEvents } from '../../hooks/use-store-events';
import type { Soldier, Vehicle } from '../../types/fleet';

export interface FleetData {
  vehicles: Vehicle[];
  soldiers: Soldier[];
  loading: boolean;
  /** Re-reads both tables — call it after every mutation. */
  reload: () => Promise<void>;
}

export function useFleet(): FleetData {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [soldiers, setSoldiers] = useState<Soldier[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    const [nextVehicles, nextSoldiers] = await Promise.all([listVehicles(), listSoldiers()]);
    setVehicles(nextVehicles);
    setSoldiers(nextSoldiers);
  }, []);

  // A fresh installation opens on the company's own roster and its own
  // twenty-eight vehicles. Seeding is additive, so it never undoes an edit or
  // brings a deleted record back.
  //
  // A device that has been running since before either list shipped has a table
  // that is not empty, so the seed alone would never reach it — that is what
  // the reconciliations ahead of them are for. Each runs once per revision
  // and never again, so from the operator's second launch onwards this costs
  // three indexed reads.
  //
  // The roles pass runs last of the roster three: it refiles whatever is on the
  // table by then, seeded records and the operator's own alike, onto the six
  // jobs the company keeps.
  useEffect(() => {
    void (async () => {
      await reconcileSoldierRoster();
      await seedSoldierRoster();
      await reconcileSoldierRoles();
      await reconcileVehicleRoster();
      await seedVehicleRoster();
      await reload();
      setLoading(false);
    })();
  }, [reload]);

  useStoreEvents(reload, ['fleet']);

  return { vehicles, soldiers, loading, reload };
}
